import { toDisplayDateInput } from "./dateUtils";
import type {
  BrandReportDownload,
  BrandReportHourCell,
  BrandReportLocationRow,
} from "../schemas/brandReportSchema";

/** Must match MAX_REPORT_DAYS on the server, which rejects anything longer. */
export const MAX_REPORT_DAYS = 366;

export type ReportPreset = "7d" | "30d" | "thisMonth" | "lastMonth" | "custom";

export const REPORT_PRESETS: Array<{ value: ReportPreset; label: string }> = [
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "thisMonth", label: "This month" },
  { value: "lastMonth", label: "Last month" },
  { value: "custom", label: "Custom" },
];

const DAY_MS = 86_400_000;

/** Calendar-day arithmetic on "YYYY-MM-DD" keys, done in UTC so it never drifts. */
const shiftDay = (key: string, days: number): string =>
  new Date(Date.parse(`${key}T00:00:00Z`) + days * DAY_MS)
    .toISOString()
    .slice(0, 10);

/**
 * The window a preset stands for, in IST calendar days — the zone the server
 * builds the report in. "Last 7 days" includes today.
 */
export const presetRange = (
  preset: Exclude<ReportPreset, "custom">,
  now: Date = new Date(),
): { startDate: string; endDate: string } => {
  const today = toDisplayDateInput(now);
  const [year, month] = today.split("-").map(Number);
  const pad = (n: number) => String(n).padStart(2, "0");

  switch (preset) {
    case "7d":
      return { startDate: shiftDay(today, -6), endDate: today };
    case "30d":
      return { startDate: shiftDay(today, -29), endDate: today };
    case "thisMonth":
      return { startDate: `${year}-${pad(month)}-01`, endDate: today };
    case "lastMonth": {
      const firstOfThis = `${year}-${pad(month)}-01`;
      const lastOfPrev = shiftDay(firstOfThis, -1);
      return { startDate: `${lastOfPrev.slice(0, 7)}-01`, endDate: lastOfPrev };
    }
  }
};

/** Why a custom window cannot be requested, or null when it can. */
export const validateRange = (startDate: string, endDate: string): string | null => {
  if (!startDate || !endDate) return "Pick both a start and an end date.";
  if (startDate > endDate) return "The start date is after the end date.";
  const days =
    (Date.parse(`${endDate}T00:00:00Z`) - Date.parse(`${startDate}T00:00:00Z`)) /
      DAY_MS +
    1;
  if (days > MAX_REPORT_DAYS) {
    return `A report can cover at most ${MAX_REPORT_DAYS} days.`;
  }
  return null;
};

/** "3 hr 04 min" for cards; seconds only when there is nothing bigger to show. */
export const formatAirtime = (seconds: number): string => {
  const total = Math.max(0, Math.round(seconds || 0));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h.toLocaleString("en-IN")} hr ${String(m).padStart(2, "0")} min`;
  if (m > 0) return `${m} min ${String(s).padStart(2, "0")} sec`;
  return `${s} sec`;
};

/** "01 Sep" for chart axes, from a calendar-day key. */
export const formatDayLabel = (key: string): string =>
  new Date(`${key}T00:00:00Z`).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    timeZone: "UTC",
  });

/** "Mon, 01 Sep 2026" for a day row. */
export const formatDayLong = (key: string): string =>
  new Date(`${key}T00:00:00Z`).toLocaleDateString("en-GB", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });

export interface CityRow {
  cityId: string | null;
  city: string;
  areas: number;
  restaurants: number;
  screens: number;
  plays: number;
  airtime: number;
}

/** The server reports per area; the city-wise report adds its areas up. */
export const groupByCity = (rows: BrandReportLocationRow[]): CityRow[] => {
  const byCity = new Map<string, CityRow>();
  for (const row of rows) {
    const key = row.cityId ?? row.city ?? "";
    const entry = byCity.get(key) ?? {
      cityId: row.cityId,
      city: row.city || "Unassigned",
      areas: 0,
      restaurants: 0,
      screens: 0,
      plays: 0,
      airtime: 0,
    };
    entry.areas += 1;
    // A restaurant sits in one area, so summing areas does not double count.
    entry.restaurants += row.restaurants;
    entry.screens += row.screens;
    entry.plays += row.plays;
    entry.airtime += row.airtime;
    byCity.set(key, entry);
  }
  return [...byCity.values()].sort((a, b) => b.plays - a.plays);
};

/** Plays in each hour of the day (IST), all weekdays together. */
export const playsByHour = (cells: BrandReportHourCell[]): number[] => {
  const hours = Array<number>(24).fill(0);
  for (const cell of cells) {
    if (cell.hour >= 0 && cell.hour <= 23) hours[cell.hour] += cell.plays;
  }
  return hours;
};

const clock = (hour: number) => `${hour % 12 || 12} ${hour % 24 < 12 ? "am" : "pm"}`;

/** "9 am – 10 am" */
export const hourLabel = (hour: number): string => `${clock(hour)} – ${clock(hour + 1)}`;

/** Hands a server-generated file to the browser as a download. */
export const saveReportFile = (file: BrandReportDownload): void => {
  const bytes = Uint8Array.from(atob(file.base64), (ch) => ch.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: file.mimeType }));
  const link = document.createElement("a");
  link.href = url;
  link.download = file.fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
